import { Grid2 } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useState } from 'react';
import InputField from 'src/components/Steps/InputField';
import Modal from 'src/components/Steps/Modal';
import { socialLinks } from 'src/config';
import Analytics from 'src/helpers/analytics';
import {
  ContactUsButton,
  ContactUsPaperPlaneStyled,
  Divider,
  FacebookMessengerFilledBlueStyled,
  FormTitle,
  InfoPagesContentContainer,
  SocialButtons,
  SocialButtonsTitle,
  WhatsappFilledBlueStyled,
} from 'src/styles/infoPages';
import { StyledForm, StyledFormItem } from 'src/styles/signup/signupSteps';
import * as Yup from 'yup';

const containerStyles = { margin: '45px 0px', padding: '0px 35px' };
const formTitleStyles: React.CSSProperties = { textAlign: 'center' };

export type ContactUsFormType = {
  name: string;
  phone: string;
  mail: string;
  message: string;
};

const initialValues: ContactUsFormType = {
  name: '',
  phone: '',
  mail: '',
  message: '',
};

const ContactUsSchema = Yup.object().shape({
  name: Yup.string().notRequired(),
  phone: Yup.string().length(10, 'authentication.errorText').notRequired(),
  mail: Yup.string().email('errors.invalidEmail').required('errors.mandatoryField'),
  message: Yup.string().required('errors.mandatoryField'),
});

const ContactUs = () => {
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation();

  const onSubmit = async (formData: any) => {
    setOpenModal(true);
    form.resetForm();
  };

  const form = useFormik({
    initialValues: { ...initialValues },
    onSubmit,
    validationSchema: ContactUsSchema,
    validateOnMount: true,
  });

  const onAppClick = (name: string) => {
    Analytics.logAppButtonClick(name);
  };

  return (
    <>
      <InfoPagesContentContainer style={containerStyles}>
        <Grid2 container spacing={5}>
          <Grid2 size={{ xs: 12, md: 5.5 }}>
            <FormTitle>{t('contactUs.formTitle')}</FormTitle>
            <StyledForm container>
              <StyledFormItem size={{ xs: 12, sm: 6 }}>
                <InputField
                  placeholder={t('contactUs.name')}
                  maxLength={30}
                  value={form.values.name}
                  onChange={form.handleChange('name')}
                  onBlur={form.handleBlur('name')}
                  error={!!form.errors.name}
                  errorText={t('errors.mandatoryField')}
                />
              </StyledFormItem>
              <StyledFormItem size={{ xs: 12, sm: 6 }}>
                <InputField
                  placeholder={t('contactUs.phone')}
                  isNumeric
                  maxLength={10}
                  value={form.values.phone}
                  onChange={form.handleChange('phone')}
                  onBlur={form.handleBlur('phone')}
                  error={!form.values.phone.startsWith('05') && form.values.phone.length > 1}
                  errorText={t('authentication.errorText')}
                />
              </StyledFormItem>
              <StyledFormItem size={{ xs: 12 }}>
                <InputField
                  placeholder={t('contactUs.mail')}
                  value={form.values.mail}
                  onChange={form.handleChange('mail')}
                  onBlur={form.handleBlur('mail')}
                  error={form.touched.mail && !!form.errors.mail}
                  errorText={t('errors.invalidEmail')}
                />
              </StyledFormItem>
              <StyledFormItem size={{ xs: 12 }}>
                <InputField
                  placeholder={t('contactUs.message')}
                  value={form.values.message}
                  onChange={form.handleChange('message')}
                  onBlur={form.handleBlur('message')}
                  error={form.touched.message && !!form.errors.message}
                  errorText={t('errors.mandatoryField')}
                  multiline
                  rows={5}
                />
              </StyledFormItem>
              <ContactUsButton onClick={form.submitForm} disabled={!form.isValid}>
                {t('contactUs.button')}
              </ContactUsButton>
            </StyledForm>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 1 }}>
            <Divider />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 5.5 }}>
            <SocialButtonsTitle>{t('contactUs.socialButtonsTitle')}</SocialButtonsTitle>
            <SocialButtons>
              <a onClick={() => onAppClick('whatsapp')} href={socialLinks.whatsapp} target="_blank" rel="noreferrer">
                <WhatsappFilledBlueStyled />
              </a>
              <a onClick={() => onAppClick('facebook')} href={socialLinks.facebook} target="_blank" rel="noreferrer">
                <FacebookMessengerFilledBlueStyled />
              </a>
            </SocialButtons>
            <ContactUsPaperPlaneStyled />
          </Grid2>
        </Grid2>
      </InfoPagesContentContainer>

      {openModal && (
        <Modal open={openModal} setIsOpen={setOpenModal} messageTitle={''}>
          <FormTitle style={formTitleStyles}>{t('contactUs.modalText')}</FormTitle>
        </Modal>
      )}
    </>
  );
};

export default ContactUs;
